import {CartLineMapper} from "@app/core/cart/infrastructure/mappers/cart-line.mapper";
import {CartPromotionMapper} from "@app/core/cart/infrastructure/mappers/cart-promotion.mapper";
import {CouponMapper} from "@app/core/coupon/infrastructure/mappers/coupon.mapper";
import {groupBy} from "@app/core/shared/domain/array.utils";
import {Cart} from "../../domain/dtos/cart";
import {CartLine} from "../../domain/dtos/cart-line";
import {CartPromotion} from "../../domain/dtos/cart-promotion";
import {CartErrorMapper} from "@app/core/cart/infrastructure/mappers/cart-error.mapper";
import {PaymentMethodTypeMapper} from "@app/core/delivery-point/infrastructure/mappers/payment-method-type.mapper";


export class CartMapper {
    static deserialize(param: any): Cart {
        const productCartLines = param.products.map(CartLineMapper.deserialize);
        const promotions = param.promotions.map(CartPromotionMapper.deserialize)
        const promotionCartLines = CartMapper.getCartLinesFromPromotions(promotions)
        return {
            //Body
            id: param.id,
            paymentMethod: PaymentMethodTypeMapper.deserialize(param.payment_method),
            date: param.date ? new Date(param.date): null, //Create Date from yyyy-mm-dd
            coupon: param.coupon ? CouponMapper.deserialize(param.coupon) : null,
            cartLines: [
                ...productCartLines,
                ...promotionCartLines,
            ],
            //Totals
            totalAccumulated: param.total_acumulated,
            totalCharge: param.total_charge,
            totalDiscount: param.total_discount,
            totalPrice: param.total_price,
            totalPriceNoTaxes: param.total_price_no_taxes,
            totalTaxes: param.total_taxes,
            errors: param.errors.map(CartErrorMapper.deserialize),
            observations: param.observations,
            shoppingOrder: param.shoppingOrder,
        }
    }

    static serialize(param: Cart): any {
        return {
            //Body
            payment_method: PaymentMethodTypeMapper.serialize(param.paymentMethod),
            date: param.date ? CartMapper.formatDate(param.date) : null,
            coupon: param.coupon ? CouponMapper.serialize(param.coupon) : null,
            products: CartMapper.getProductsFromCartLines(param.cartLines).map(CartLineMapper.serialize),
            promotions: CartMapper.getPromotionsFromCartLines(param.cartLines).map(CartPromotionMapper.serialize),
            //Totals
            total_price_no_taxes: param.totalPriceNoTaxes,
            total_discount: param.totalDiscount,
            total_charge: param.totalCharge,
            total_taxes: param.totalTaxes,
            total_acumulated: param.totalAccumulated,
            total_price: param.totalPrice,
            observations: [null,undefined,''].includes(param.observations) ? null : param.observations, //Send null if observations is equal to '' , null or undefined.
            shoppingOrder: param.shoppingOrder == null ? null : param.shoppingOrder, //Send null if shoppingOrder is equal to null or undefined.
        };
    }

    /**
     * Convert Date to yyyy-mm-dd format
     * @param date
     * @private
     */
    private static formatDate(date: Date): string {
        const dateWithoutTimeZone = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        return dateWithoutTimeZone.toISOString().split('T')[0]
    }

    private static getProductsFromCartLines(cartLines: CartLine[]): CartLine[] {
        return [...cartLines].filter(cartLine => cartLine.promotionCode === '');
    }

    private static getPromotionsFromCartLines(cartLines: CartLine[]): CartPromotion[] {
        const promotionCartLines = [...cartLines].filter(cartLine => cartLine.promotionCode !== '');
        return Object.entries(groupBy(promotionCartLines, 'promotionCode'))
            .map(([promotionCode, cartLinesArray]) => {
                const totalPrice = cartLinesArray.reduce(
                    //Calculate the total price of the promotion, multiplying the price of each product by the quantity.
                    (accumulator, cartLine) => accumulator + (cartLine.price * cartLine.quantity),0
                );
                return {
                    code: promotionCode,
                    products: cartLinesArray,
                    // We need to round the total price to 2 decimals
                    totalPrice: Number(totalPrice.toFixed(2)),
                }
            })
    }

    private static getCartLinesFromPromotions(promotions: CartPromotion[]): CartLine[] {
        const cartLines: CartLine[] = []
        promotions.forEach(promotion => {
            const promotionCartLines: CartLine[] = promotion.products.map(
                product => ({ ...product, promotionCode: promotion.code })
            )
            cartLines.push(...promotionCartLines);
        });
        return cartLines;
    }

}
